import { compareVersions, unseenVersions, type ChangelogVersion } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { z } from 'zod';
import { api } from '../api';
import { APP_VERSION } from '../appVersion';
import { readNewsTabs, tabsWithNews, updateOffer, writeNewsTabs } from '../nouveautes';
import { useAuthStore } from './auth';
import { useOfflineStore } from './offline';
import { useStreamStore } from './stream';

/** Contrôle régulier de la version du serveur (en plus du retour sur l'onglet et de la reconnexion du flux). */
export const CHECK_EVERY_MS = 5 * 60_000;
/** Au retour sur l'onglet : pas plus d'un contrôle par minute. */
export const FOCUS_CHECK_MIN_MS = 60_000;
/** « Rafraîchir » attend au plus ce temps la mise à jour du service worker avant de recharger. */
const SW_UPDATE_TIMEOUT_MS = 3000;

const VersionEvent = z.object({ version: z.string() });

/** Rechargement de la page (remplacé par les tests). */
export const pageReload = { run: () => window.location.reload() };

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Lot BA « Nouveautés » : encart « Nouvelle version » (bas gauche), popup « Quoi de neuf » (bas droite), point vert
 * des onglets et données de la page Nouveautés. Démarré par la coquille (jamais en Mode écran).
 */
export const useNouveautesStore = defineStore('nouveautes', () => {
  const versions = ref<ChangelogVersion[]>([]);
  /** Version du serveur du journal chargé. */
  const changelogOf = ref<string | null>(null);
  const loaded = ref(false);

  /** Dernière version vue au moment de l'ouverture de l'appli (« Nouveau pour toi » de la page). */
  const seenAtStart = ref<string | null>(null);
  const whatsNew = ref<ChangelogVersion[]>([]);
  const whatsNewOpen = ref(false);

  const serverVersion = ref<string | null>(null);
  const offer = ref<{ version: string; summary: string } | null>(null);
  const offerHidden = ref(false);
  const refreshing = ref(false);

  const newsTabs = ref<string[]>([]);
  let userId: string | null = null;

  const offerVisible = computed(() => offer.value !== null && !offerHidden.value);
  /** Versions marquées « Nouveau pour toi » sur la page Nouveautés. */
  const unseenOnPage = computed(
    () => new Set(unseenVersions(versions.value, seenAtStart.value, APP_VERSION).map((v) => v.version)),
  );

  async function loadChangelog(): Promise<void> {
    const body = await api.changelog();
    versions.value = body.versions;
    changelogOf.value = body.current;
    loaded.value = true;
  }

  function setNewsTabs(tabs: string[]): void {
    newsTabs.value = [...new Set(tabs)];
    if (userId) writeNewsTabs(storage(), userId, newsTabs.value);
  }

  /** L'onglet a été ouvert : son point vert s'éteint. */
  function visitTab(name: string | null | undefined): void {
    if (name && newsTabs.value.includes(name)) setNewsTabs(newsTabs.value.filter((t) => t !== name));
  }

  /** Ouverture de l'appli : « Quoi de neuf » si des versions visibles sont plus récentes que la dernière vue. */
  async function openWhatsNew(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.me) return;
    userId = auth.me.id;
    newsTabs.value = readNewsTabs(storage(), userId);
    seenAtStart.value = auth.me.preferences.lastSeenVersion;
    try {
      await loadChangelog();
    } catch {
      return; // Le journal est un plus : sans lui, rien ne s'affiche.
    }
    const unseen = unseenVersions(versions.value, seenAtStart.value, APP_VERSION);
    whatsNew.value = unseen;
    whatsNewOpen.value = unseen.length > 0;
    if (unseen.length > 0) setNewsTabs([...newsTabs.value, ...tabsWithNews(unseen)]);
  }

  /** « J'ai vu », ×, Échap ou « Toutes les nouveautés » : la dernière version montrée est notée comme vue. */
  async function dismissWhatsNew(): Promise<void> {
    whatsNewOpen.value = false;
    const shown = whatsNew.value
      .map((v) => v.version)
      .sort(compareVersions)
      .at(-1);
    const auth = useAuthStore();
    if (!shown || !auth.me) return;
    const current = auth.me.preferences.lastSeenVersion;
    if (current && compareVersions(current, shown) >= 0) return;
    try {
      const preferences = await api.updatePreferences({ lastSeenVersion: shown });
      if (auth.me) auth.me = { ...auth.me, preferences };
    } catch {
      // Sans réseau : le popup reviendra à la prochaine ouverture, rien n'est perdu.
    }
  }

  /** Compare la version du serveur à celle de l'appli ouverte (jamais de rechargement automatique). */
  async function check(known?: string): Promise<void> {
    if (!useOfflineStore().online) return;
    let version = known;
    if (!version) {
      try {
        version = (await api.authConfig()).build?.version;
      } catch {
        return;
      }
    }
    if (!version) return;
    serverVersion.value = version;
    if (compareVersions(version, APP_VERSION) <= 0) {
      offer.value = null;
      return;
    }
    if (changelogOf.value !== version) {
      try {
        await loadChangelog();
      } catch {
        return;
      }
    }
    offer.value = updateOffer(APP_VERSION, version, versions.value);
    // « Plus tard » : l'encart revient au contrôle suivant.
    offerHidden.value = false;
  }

  function later(): void {
    offerHidden.value = true;
  }

  /** « Rafraîchir » : met à jour le service worker (au plus 3 s), puis recharge la page. */
  async function refresh(): Promise<void> {
    refreshing.value = true;
    try {
      const registration = await navigator.serviceWorker?.getRegistration?.();
      if (registration)
        await Promise.race([
          registration.update(),
          new Promise((resolve) => setTimeout(resolve, SW_UPDATE_TIMEOUT_MS)),
        ]);
    } catch {
      // Sans service worker à jour, le rechargement passe quand même par le réseau (pages : réseau d'abord).
    }
    pageReload.run();
  }

  let timer: ReturnType<typeof setInterval> | undefined;
  let lastFocusCheck = 0;
  let offVersion: (() => void) | null = null;
  const onFocus = () => {
    if (document.visibilityState === 'hidden') return;
    if (Date.now() - lastFocusCheck < FOCUS_CHECK_MIN_MS) return;
    lastFocusCheck = Date.now();
    void check();
  };

  function start(): void {
    stop();
    void openWhatsNew();
    const auth = useAuthStore();
    lastFocusCheck = Date.now();
    void check(auth.config?.build?.version);
    timer = setInterval(() => void check(), CHECK_EVERY_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // Une mise en ligne coupe le flux : à la reconnexion, le serveur donne sa version.
    offVersion = useStreamStore().on('version', VersionEvent, (data) => void check(data.version));
  }

  function stop(): void {
    clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
    offVersion?.();
    offVersion = null;
  }

  return {
    versions,
    loaded,
    seenAtStart,
    whatsNew,
    whatsNewOpen,
    serverVersion,
    offer,
    offerVisible,
    refreshing,
    newsTabs,
    unseenOnPage,
    loadChangelog,
    openWhatsNew,
    dismissWhatsNew,
    visitTab,
    check,
    later,
    refresh,
    start,
    stop,
  };
});
